import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleLink,
  type ScheduleSettings,
  type HolidayInfo,
  formatDateFull,
  formatDateLabel,
  getDelayStatusColor,
  parseISODate,
  toISODate,
} from '@/lib/scheduleUtils';
import ScheduleGanttView from './ScheduleGanttView';
import type { AuthProfile } from '@/hooks/useAuth';
import SubcontractorScheduleUpdates from './SubcontractorScheduleUpdates';

interface Props {
  items: ScheduleItem[];
  links: ScheduleLink[];
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  profile: AuthProfile | null;
  loading: boolean;
}

type SubStatusKey = 'on_track' | 'coming_up' | 'due_soon' | 'delayed' | 'completed' | 'attention';

function getSubStatus(item: ScheduleItem, today: string): SubStatusKey {
  if (item.is_complete) return 'completed';
  const risk = item.delay_status;
  if (risk === 'delayed' || risk === 'completed_late') return 'delayed';
  if (risk === 'at_risk') return 'attention';
  if (!item.start_date) return 'coming_up';
  const start = item.start_date;
  const end = item.end_date || item.start_date;
  const todayMs = parseISODate(today).getTime();
  const startMs = parseISODate(start).getTime();
  const endMs = parseISODate(end).getTime();
  const threeDays = 3 * 24 * 60 * 60 * 1000;
  if (endMs < todayMs) return 'delayed';
  if (endMs - todayMs <= threeDays) return 'due_soon';
  if (startMs <= todayMs) return 'on_track';
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  if (startMs - todayMs <= sevenDays) return 'coming_up';
  return 'coming_up';
}

const STATUS_CONFIG: Record<SubStatusKey, { label: string; bg: string; text: string; border: string; icon: string }> = {
  on_track:  { label: 'On Track',        bg: '#F0FDF4', text: '#059669', border: '#BBF7D0', icon: 'ri-checkbox-circle-line' },
  coming_up: { label: 'Coming Up',       bg: '#F9FAFB', text: '#6B7280', border: '#E5E7EB', icon: 'ri-time-line' },
  due_soon:  { label: 'Due Soon',        bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74', icon: 'ri-alarm-warning-line' },
  delayed:   { label: 'Delayed',         bg: '#FEF2F2', text: '#EF4444', border: '#FECACA', icon: 'ri-error-warning-line' },
  completed: { label: 'Completed',       bg: '#F0FDF4', text: '#059669', border: '#BBF7D0', icon: 'ri-check-double-line' },
  attention: { label: 'Attention Needed',bg: '#FFFBEB', text: '#F59E0B', border: '#FDE68A', icon: 'ri-alert-line' },
};

interface ItemDetailModalProps {
  item: ScheduleItem;
  links: ScheduleLink[];
  allItems: ScheduleItem[];
  onClose: () => void;
}

function ItemDetailModal({ item, links, allItems, onClose }: ItemDetailModalProps) {
  const { t } = useTranslation();
  const today = toISODate(new Date());
  const status = getSubStatus(item, today);
  const cfg = STATUS_CONFIG[status];
  const itemLinks = links.filter((l) => l.predecessor_item_id === item.id || l.successor_item_id === item.id);

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: cfg.bg, color: cfg.text }}
            >
              {cfg.label}
            </span>
            <h3 className="text-[#1A1A1A] font-bold text-sm truncate">{item.title}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer flex-shrink-0 ml-2">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {item.phase && (
            <div className="flex items-center gap-2">
              <i className="ri-folder-line text-[#9CA3AF] text-sm"></i>
              <span className="text-[#6B7280] text-sm">{item.phase}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('subView.start')}</p>
              <p className="text-[#374151] text-sm font-semibold">{formatDateFull(item.start_date)}</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('subView.end')}</p>
              <p className="text-[#374151] text-sm font-semibold">{formatDateFull(item.end_date)}</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('subView.duration')}</p>
              <p className="text-[#374151] text-sm font-semibold">{item.duration_days} {t('subView.workingDays')}</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('subView.progress')}</p>
              <p className="text-[#374151] text-sm font-semibold">{item.progress}%</p>
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${item.progress}%`, backgroundColor: item.is_complete ? '#059669' : '#E8621A' }}
              />
            </div>
          </div>

          {(item.assigned_role || item.assigned_to) && (
            <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl p-3">
              <i className="ri-user-line text-[#9CA3AF] text-sm"></i>
              <div>
                {item.assigned_role && <p className="text-[#374151] text-sm font-semibold">{item.assigned_role}</p>}
                {item.assigned_to && <p className="text-[#9CA3AF] text-xs">{item.assigned_to}</p>}
              </div>
            </div>
          )}

          {item.delay_status !== 'on_track' && item.delay_status !== 'completed_on_time' && (
            <div
              className="rounded-xl p-3 border"
              style={{ backgroundColor: getDelayStatusColor(item.delay_status).bg, borderColor: getDelayStatusColor(item.delay_status).text + '40' }}
            >
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-alert-line text-sm" style={{ color: getDelayStatusColor(item.delay_status).text }}></i>
                <span className="text-xs font-bold" style={{ color: getDelayStatusColor(item.delay_status).text }}>
                  {item.delay_status === 'delayed' ? t('subView.delayed') : item.delay_status === 'at_risk' ? t('subView.atRisk') : item.delay_status}
                </span>
              </div>
              {item.delay_reason && (
                <p className="text-[#6B7280] text-xs leading-relaxed">{item.delay_reason}</p>
              )}
            </div>
          )}

          {item.notes && (
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('subView.notes')}</p>
              <p className="text-[#6B7280] text-sm leading-relaxed">{item.notes}</p>
            </div>
          )}

          {itemLinks.length > 0 && (
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-2">{t('subView.linkedStages')}</p>
              <div className="space-y-1.5">
                {itemLinks.map((l) => {
                  const isPre = l.predecessor_item_id === item.id;
                  const otherId = isPre ? l.successor_item_id : l.predecessor_item_id;
                  const other = allItems.find((i) => i.id === otherId);
                  return (
                    <div key={l.id} className="flex items-center gap-2 text-xs">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-[#374151]">
                        {isPre ? t('clientView.next') : t('clientView.previous')}
                      </span>
                      <span className="text-[#374151]">{other?.title || 'Unknown'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-[#F9FAFB] text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
          >
            {t('subView.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ScheduleCardProps {
  item: ScheduleItem;
  today: string;
  onClick: () => void;
}

function ScheduleCard({ item, today, onClick }: ScheduleCardProps) {
  const { t } = useTranslation();
  const status = getSubStatus(item, today);
  const cfg = STATUS_CONFIG[status];

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer hover:border-gray-200 transition-colors"
      style={{ borderLeftWidth: 3, borderLeftColor: cfg.text }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {item.phase && (
              <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{item.phase}</span>
            )}
            {item.task_no && (
              <span className="text-[10px] text-[#D1D5DB]">#{item.task_no}</span>
            )}
          </div>
          <p className="text-[#1A1A1A] text-sm font-semibold leading-snug">{item.title}</p>
        </div>
        <span
          className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ backgroundColor: cfg.bg, color: cfg.text }}
        >
          {cfg.label}
        </span>
      </div>

      <div className="flex items-center gap-3 text-xs text-[#9CA3AF] flex-wrap">
        <span className="flex items-center gap-1">
          <i className="ri-calendar-line text-[10px]"></i>
          {formatDateLabel(item.start_date)} → {formatDateLabel(item.end_date)}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-time-line text-[10px]"></i>
          {item.duration_days}d
        </span>
        {(item.assigned_role || item.assigned_to) && (
          <span className="flex items-center gap-1">
            <i className="ri-user-line text-[10px]"></i>
            {item.assigned_role || item.assigned_to}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[#9CA3AF]">{t('subView.progress')}</span>
          <span className="text-[10px] font-bold" style={{ color: item.is_complete ? '#059669' : '#E8621A' }}>{item.progress}%</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${item.progress}%`, backgroundColor: item.is_complete ? '#059669' : '#E8621A' }}
          />
        </div>
      </div>

      {(item.schedule_risk_level !== 'normal' || item.delay_status === 'at_risk' || item.delay_status === 'delayed') && (
        <div className="mt-2 flex items-center gap-1.5">
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: item.delay_status === 'delayed' ? '#FEF2F2' : item.delay_status === 'at_risk' ? '#FEF0E7' : '#F9FAFB',
              color: item.delay_status === 'delayed' ? '#EF4444' : item.delay_status === 'at_risk' ? '#92400E' : '#6B7280',
            }}
          >
            {item.delay_status === 'delayed' ? t('subView.delayed') : item.delay_status === 'at_risk' ? t('subView.atRisk') : item.schedule_risk_level}
          </span>
          {item.delay_reason && (
            <span className="text-[9px] text-[#6B7280] truncate">{item.delay_reason}</span>
          )}
        </div>
      )}
    </button>
  );
}

export default function ScheduleSubcontractorView({ items, links, settings, holidays, profile, loading }: Props) {
  const { t } = useTranslation();
  const [myScheduleOnly, setMyScheduleOnly] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'gantt'>('cards');
  const [ganttExpanded, setGanttExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const today = toISODate(new Date());

  const hasAssignmentData = useMemo(() => {
    return items.some((i) => i.assigned_role || i.assigned_to);
  }, [items]);

  const companyName = profile?.company_name?.toLowerCase() || '';

  const myItems = useMemo(() => {
    if (!companyName) return items;
    return items.filter((i) =>
      i.assigned_to?.toLowerCase().includes(companyName) ||
      i.assigned_role?.toLowerCase().includes(companyName)
    );
  }, [items, companyName]);

  const displayItems = useMemo(() => {
    const base = (myScheduleOnly && hasAssignmentData) ? myItems : items;
    return base.filter((i) => i.is_included);
  }, [items, myItems, myScheduleOnly, hasAssignmentData]);

  // Group items
  const groups = useMemo(() => {
    const todayMs = parseISODate(today).getTime();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const fourteenDays = 14 * 24 * 60 * 60 * 1000;

    const delayed: ScheduleItem[] = [];
    const attention: ScheduleItem[] = [];
    const thisWeek: ScheduleItem[] = [];
    const nextTwoWeeks: ScheduleItem[] = [];
    const upcoming: ScheduleItem[] = [];
    const completed: ScheduleItem[] = [];

    for (const item of displayItems) {
      const status = getSubStatus(item, today);
      if (status === 'completed') { completed.push(item); continue; }
      if (status === 'delayed') { delayed.push(item); continue; }
      if (status === 'attention') { attention.push(item); continue; }

      const startMs = item.start_date ? parseISODate(item.start_date).getTime() : null;
      const endMs = item.end_date ? parseISODate(item.end_date).getTime() : null;

      if (startMs !== null && endMs !== null) {
        // Active or starting this week
        if (startMs <= todayMs + sevenDays && endMs >= todayMs) {
          thisWeek.push(item);
        } else if (startMs <= todayMs + fourteenDays) {
          nextTwoWeeks.push(item);
        } else {
          upcoming.push(item);
        }
      } else {
        upcoming.push(item);
      }
    }

    return { delayed, attention, thisWeek, nextTwoWeeks, upcoming, completed };
  }, [displayItems, today]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-[#059669] text-3xl"></i>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-[#F9FAFB]">
          <i className="ri-calendar-schedule-line text-[#D1D5DB] text-3xl"></i>
        </div>
        <h3 className="text-[#1A1A1A] font-bold text-base mb-2">{t('subView.noScheduleYet')}</h3>
        <p className="text-[#9CA3AF] text-sm max-w-xs mx-auto">
          {t('subView.noScheduleYetDesc')}
        </p>
      </div>
    );
  }

  // Build STATUS_CONFIG with translated labels
  const STATUS_CONFIG_TRANSLATED: Record<SubStatusKey, { label: string; bg: string; text: string; border: string; icon: string }> = {
    on_track:  { label: t('subView.onTrack'),        bg: '#F0FDF4', text: '#059669', border: '#BBF7D0', icon: 'ri-checkbox-circle-line' },
    coming_up: { label: t('subView.comingUp'),       bg: '#F9FAFB', text: '#6B7280', border: '#E5E7EB', icon: 'ri-time-line' },
    due_soon:  { label: t('subView.dueSoon'),        bg: '#FEF0E7', text: '#E8621A', border: '#FDBA74', icon: 'ri-alarm-warning-line' },
    delayed:   { label: t('subView.delayed'),        bg: '#FEF2F2', text: '#EF4444', border: '#FECACA', icon: 'ri-error-warning-line' },
    completed: { label: t('subView.completed'),      bg: '#F0FDF4', text: '#059669', border: '#BBF7D0', icon: 'ri-check-double-line' },
    attention: { label: t('subView.attentionNeeded'),bg: '#FFFBEB', text: '#F59E0B', border: '#FDE68A', icon: 'ri-alert-line' },
  };

  const sectionGroups = [
    { key: 'delayed',      label: t('subView.delayedAttention'), items: [...groups.delayed, ...groups.attention], icon: 'ri-error-warning-line', color: '#EF4444', bg: '#FEF2F2' },
    { key: 'thisWeek',     label: t('subView.thisWeek'),         items: groups.thisWeek,     icon: 'ri-calendar-check-line', color: '#E8621A', bg: '#FEF0E7' },
    { key: 'nextTwoWeeks', label: t('subView.nextTwoWeeks'),     items: groups.nextTwoWeeks, icon: 'ri-calendar-2-line',     color: '#374151', bg: '#F9FAFB' },
    { key: 'upcoming',     label: t('subView.upcomingWork'),     items: groups.upcoming,     icon: 'ri-time-line',           color: '#6B7280', bg: '#F9FAFB' },
    { key: 'completed',    label: t('subView.completed'),        items: groups.completed,    icon: 'ri-check-double-line',   color: '#059669', bg: '#F0FDF4' },
  ];

  return (
    <div className="h-full flex flex-col min-h-0 px-4 py-4 overflow-y-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-[#1A1A1A] font-bold text-base">{t('subView.projectSchedule')}</h3>
          <p className="text-[#9CA3AF] text-xs mt-0.5">
            {t('subView.scheduleItemsCount', { count: displayItems.length })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* My / Full toggle */}
          {hasAssignmentData && (
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setMyScheduleOnly(true)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${myScheduleOnly ? 'bg-white text-[#1A1A1A]' : 'text-[#6B7280]'}`}
              >
                {t('subView.mySchedule')}
              </button>
              <button
                onClick={() => setMyScheduleOnly(false)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${!myScheduleOnly ? 'bg-white text-[#1A1A1A]' : 'text-[#6B7280]'}`}
              >
                {t('subView.fullProject')}
              </button>
            </div>
          )}
          {/* Cards / Gantt toggle (desktop only) */}
          {!isMobile && (
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${viewMode === 'cards' ? 'bg-white text-[#1A1A1A]' : 'text-[#6B7280]'}`}
              >
                {t('subView.cards')}
              </button>
              <button
                onClick={() => setViewMode('gantt')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${viewMode === 'gantt' ? 'bg-white text-[#1A1A1A]' : 'text-[#6B7280]'}`}
              >
                {t('subView.gantt')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stage context for subcontractor */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">{t('subView.stageContext')}</h4>
        <div className="flex items-center gap-2 flex-wrap">
          {(() => {
            // Compute stage summary
            const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];
            const STAGE_CONFIG: Record<string, { accent: string; bg: string }> = {
              Planning:     { accent: '#D97706', bg: '#FFFBEB' },
              Design:       { accent: '#0D9488', bg: '#ECFDF5' },
              Consent:      { accent: '#E8621A', bg: '#FEF0E7' },
              Construction: { accent: '#059669', bg: '#F0FDF4' },
              Handover:     { accent: '#10B981', bg: '#ECFDF5' },
            };
            function inferStageFromTitle(title: string): string {
              const lower = title.toLowerCase();
              if (/\b(precon|contract|owner|site prep|service disconnect|h&s|health and safety|setup|kickoff|application|deposit)\b/.test(lower)) return 'Planning';
              if (/\b(design|engineering|geotech|drawings?|specification|architect|structural engineer)\b/.test(lower)) return 'Design';
              if (/\b(epa|council|cc inspection|eg inspection|approval|compliance|consent|building consent|resource consent)\b/.test(lower)) return 'Consent';
              if (/\b(demolition|excavation|foundation|slab|drain|plumbing|frame|truss|roof|cladding|gib|paint|tile|kitchen|flooring|electrical|interior|clean|wrap|window|brick|plaster|retaining wall|joist|steel)\b/.test(lower)) return 'Construction';
              if (/\b(final inspection|owner inspection|warranty|photo|ccc|handover|defect|maintenance period|practical completion|insurance change)\b/.test(lower)) return 'Handover';
              return 'Planning';
            }
            const map = new Map<string, { total: number; completed: number }>();
            for (const name of STAGE_ORDER) map.set(name, { total: 0, completed: 0 });
            for (const item of displayItems) {
              const stageName = item.main_stage || (item.phase && STAGE_CONFIG[item.phase] ? item.phase : inferStageFromTitle(item.title));
              const s = map.get(stageName);
              if (!s) continue;
              s.total += 1;
              if (item.is_complete || item.progress >= 100) s.completed += 1;
            }
            const activeStage = STAGE_ORDER.find((name) => {
              const d = map.get(name)!;
              return d.total > 0 && d.completed < d.total;
            });
            return STAGE_ORDER.map((name) => {
              const d = map.get(name)!;
              if (d.total === 0) return null;
              const cfg = STAGE_CONFIG[name];
              const isActive = activeStage === name;
              const isDone = d.completed === d.total;
              return (
                <span
                  key={name}
                  className="text-[10px] font-bold px-2 py-1 rounded-full"
                  style={{
                    backgroundColor: isActive ? cfg.bg : isDone ? '#F0FDF4' : '#F9FAFB',
                    color: isActive ? cfg.accent : isDone ? '#059669' : '#9CA3AF',
                  }}
                >
                  {name}: {d.completed}/{d.total}
                </span>
              );
            }).filter(Boolean);
          })()}
        </div>
      </div>

      {/* My schedule info banner */}
      {myScheduleOnly && myItems.length === 0 && (
        <div className="bg-[#FEF0E7] rounded-2xl p-4 border border-[#FDBA74]">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-information-line text-[#E8621A]"></i>
            <span className="text-[#E8621A] text-sm font-bold">{t('subView.noAssigned')}</span>
          </div>
          <p className="text-[#374151] text-sm">
            {t('subView.noAssignedDesc')}
          </p>
        </div>
      )}

      {/* Read-only notice */}
      <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-xl px-3 py-2.5">
        <i className="ri-eye-line text-[#9CA3AF] text-sm"></i>
        <span className="text-[#9CA3AF] text-xs">{t('subView.readOnly')}</span>
      </div>

      {/* ── GANTT VIEW (desktop) ── */}
      {viewMode === 'gantt' && !isMobile && displayItems.some((i) => i.start_date) && (
        <div className={ganttExpanded ? 'fixed inset-0 z-[100] bg-white flex flex-col' : 'flex flex-col min-h-[480px] h-[min(72vh,720px)]'}>
          <div className={`flex items-center justify-end gap-2 ${ganttExpanded ? 'px-4 py-2 border-b border-gray-100 flex-shrink-0' : 'mb-2'}`}>
            <button
              type="button"
              onClick={() => setGanttExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-[#374151] bg-white hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className={ganttExpanded ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}></i>
              {ganttExpanded ? t('subView.exitFullScreen', { defaultValue: 'Exit full screen' }) : t('subView.fullScreen', { defaultValue: 'Full screen' })}
            </button>
          </div>
          <div className={`flex-1 min-h-0 ${ganttExpanded ? '' : 'border border-gray-100 rounded-xl overflow-hidden'}`}>
            <ScheduleGanttView
              items={displayItems}
              allItems={displayItems}
              links={links}
              showAllLinks
              settings={settings}
              holidays={holidays}
              criticalItemIds={new Set()}
              computedRisk={new Map()}
              canEdit={false}
              showIncludeColumn={false}
              listLayout="compact"
              fillHeight
              isFullscreen={ganttExpanded}
              onEdit={(item) => setSelectedItem(item)}
              onToggleComplete={() => {}}
              onToggleIncluded={() => {}}
              onIncludeAll={() => {}}
              onExcludeAll={() => {}}
            />
          </div>
        </div>
      )}

      {viewMode === 'gantt' && !isMobile && !displayItems.some((i) => i.start_date) && (
        <div className="text-center py-10 bg-[#F9FAFB] rounded-xl border border-gray-100">
          <p className="text-[#9CA3AF] text-sm">{t('subView.noDatesForGantt', { defaultValue: 'No dated schedule items to display in Gantt view.' })}</p>
        </div>
      )}

      {/* ── CARDS VIEW ── */}
      {(viewMode === 'cards' || isMobile) && (
        <div className="space-y-5">
          {sectionGroups.map((group) => {
            if (group.items.length === 0) return null;
            return (
              <div key={group.key}>
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ backgroundColor: group.bg }}
                  >
                    <i className={`${group.icon} text-xs`} style={{ color: group.color }}></i>
                  </div>
                  <h4 className="text-sm font-bold text-[#1A1A1A]">{group.label}</h4>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: group.bg, color: group.color }}
                  >
                    {group.items.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <ScheduleCard
                      key={item.id}
                      item={item}
                      today={today}
                      onClick={() => setSelectedItem(item)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {displayItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#9CA3AF] text-sm">{t('subView.noItemsToDisplay')}</p>
            </div>
          )}
        </div>
      )}

      {/* ── Schedule Updates (relevant changes) ── */}
      <SubcontractorScheduleUpdates
        projectId={items[0]?.project_id || ''}
        companyName={profile?.company_name || null}
      />

      {/* Item detail modal */}
      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          links={links}
          allItems={items}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}
